import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Slider, Typography, Paper } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';

/**
 * Audio player component for Benny's Journal entries
 */
const AudioPlayer = ({ audioURL, title = "Audio Recording" }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => {
      if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('durationchange', updateDuration);
    audio.addEventListener('canplay', updateDuration);
    audio.addEventListener('ended', handleEnded);

    // Force initial duration check
    if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
      setDuration(audio.duration);
    }

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('durationchange', updateDuration);
      audio.removeEventListener('canplay', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioURL]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeChange = (event, newValue) => {
    const audio = audioRef.current;
    audio.currentTime = newValue;
    setCurrentTime(newValue);
  };

  const handleVolumeChange = (event, newValue) => {
    const audio = audioRef.current;
    audio.volume = newValue;
    setVolume(newValue);
    setIsMuted(newValue === 0);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (isMuted) {
      audio.volume = volume || 0.5;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        backgroundColor: '#f5f5dc',
        border: '2px solid #8B4513',
        borderRadius: 2,
      }}
    >
      <audio ref={audioRef} src={audioURL} preload="metadata" />
      
      <Typography
        variant="subtitle2"
        sx={{
          fontFamily: '"Georgia", serif',
          color: '#5c3c10',
          mb: 1,
          fontWeight: 'bold',
        }}
      >
        🎙️ {title}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          onClick={togglePlay}
          sx={{
            backgroundColor: '#8B4513',
            color: '#F5F5F5',
            '&:hover': {
              backgroundColor: '#A0522D',
            },
          }}
        >
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>

        <Typography
          variant="caption"
          sx={{
            fontFamily: '"Georgia", serif',
            color: '#5c3c10',
            minWidth: '40px',
          }}
        >
          {formatTime(currentTime)}
        </Typography>

        <Slider
          value={currentTime}
          max={duration || 100}
          onChange={handleTimeChange}
          sx={{
            flex: 1,
            color: '#8B4513',
            '& .MuiSlider-thumb': {
              backgroundColor: '#8B4513',
            },
            '& .MuiSlider-track': {
              backgroundColor: '#8B4513',
            },
            '& .MuiSlider-rail': {
              backgroundColor: '#d2b48c',
            },
          }}
        />

        <Typography
          variant="caption"
          sx={{
            fontFamily: '"Georgia", serif',
            color: '#5c3c10',
            minWidth: '40px',
          }}
        >
          {formatTime(duration)}
        </Typography>

        <IconButton
          onClick={toggleMute}
          size="small"
          sx={{
            color: '#8B4513',
          }}
        >
          {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
        </IconButton>

        <Slider
          value={isMuted ? 0 : volume}
          max={1}
          step={0.1}
          onChange={handleVolumeChange}
          sx={{
            width: 80,
            color: '#8B4513',
            '& .MuiSlider-thumb': {
              backgroundColor: '#8B4513',
            },
            '& .MuiSlider-track': {
              backgroundColor: '#8B4513',
            },
            '& .MuiSlider-rail': {
              backgroundColor: '#d2b48c',
            },
          }}
        />
      </Box>
    </Paper>
  );
};

export default AudioPlayer;
